/**
 * canvasFontCache.ts — patches CanvasRenderingContext2D.font setter to skip
 * no-op writes (assigning the same string twice in a row).
 *
 * BETA 14.50 — Chrome DevTools profile showed `set font` at 7.8% of total CPU
 * during gameplay (4,682ms self-time across a 60s recording). The codebase has
 * 30+ direct `ctx.font = '...'` assignments scattered across balancePanel,
 * chatSystem, engine HUD, debug overlay, etc. Each assignment triggers Chrome's
 * font-parse + layout invalidation even when the value is identical to the
 * current font. This patch dedupes them transparently — every existing call
 * site is unchanged; the overhead just disappears.
 *
 * Implementation: replaces the prototype's `font` accessor with a passthrough
 * getter and a deduping setter backed by a per-canvas WeakMap. Reads work
 * exactly as before. Writes only commit if the new value differs from the last
 * value seen on that specific canvas context. The WeakMap lets contexts get
 * GC'd normally without leaking entries.
 *
 * Side-effect import: importing this file once (at engine.ts top-level) installs
 * the patch globally. Safe to import multiple times — defineProperty replacement
 * is idempotent if the patch is already in place (we guard via a marker prop).
 */

const PATCH_MARKER = '__bppFontCachePatched__';

interface PatchedProto extends CanvasRenderingContext2D {
  [PATCH_MARKER]?: boolean;
}

(function installFontCachePatch() {
  const proto = CanvasRenderingContext2D.prototype as PatchedProto;
  if (proto[PATCH_MARKER]) return; // already patched (idempotent)

  const origDescriptor = Object.getOwnPropertyDescriptor(
    CanvasRenderingContext2D.prototype,
    'font',
  );
  if (!origDescriptor || !origDescriptor.set || !origDescriptor.get) {
    // Browser doesn't expose font as a settable accessor on the prototype —
    // bail silently. Game still works; we just don't get the perf win.
    return;
  }

  const origSet = origDescriptor.set;
  const origGet = origDescriptor.get;
  const fontByCtx = new WeakMap<CanvasRenderingContext2D, string>();

  Object.defineProperty(CanvasRenderingContext2D.prototype, 'font', {
    configurable: true,
    enumerable: true,
    get(this: CanvasRenderingContext2D) {
      return origGet.call(this);
    },
    set(this: CanvasRenderingContext2D, value: string) {
      const last = fontByCtx.get(this);
      if (last === value) return; // identical font — skip the expensive parse
      origSet.call(this, value);
      fontByCtx.set(this, value);
    },
  });

  Object.defineProperty(proto, PATCH_MARKER, {
    value: true,
    writable: false,
    enumerable: false,
    configurable: false,
  });
})();
